// ChangeAccount.js - FINAL VERSION (Same UI + Auto Fetch Current Case)
import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { GrCircleInformation } from "react-icons/gr";
import axios from "axios";

const ChangeAccount = ({ show, handleClose, caseNumber, handleConfirm }) => {
  const [accounts, setAccounts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [currentCase, setCurrentCase] = useState(null);
  const [loading, setLoading] = useState(false);
  const [providerLoading, setProviderLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [providerError, setProviderError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("");

  // Function to get userLabId from localStorage
  const getUserLabId = () => {
    try {
      // Try different possible keys in localStorage
      const possibleKeys = ['user', 'userData', 'auth', 'currentUser', 'labUser', 'labUserData'];
      
      for (const key of possibleKeys) {
        const storedData = localStorage.getItem(key);
        if (storedData) {
          try {
            const parsed = JSON.parse(storedData);
            
            // Check for userLabId in different possible locations
            const userLabId = 
              parsed.userLabId || 
              parsed.labId || 
              parsed.id || 
              (parsed.data && parsed.data.userLabId) ||
              (parsed.data && parsed.data.labId) ||
              (parsed.user && parsed.user.userLabId) ||
              (parsed.user && parsed.user.labId);
            
            if (userLabId) {
              console.log(`Found userLabId in key "${key}":`, userLabId);
              return userLabId;
            }
            
            // Check if the value itself is the labId
            if (typeof storedData === 'string' && storedData.match(/^000000\d{2}$/)) {
              console.log('Found labId as direct string:', storedData);
              return storedData;
            }
          } catch (e) {
            console.warn(`Could not parse localStorage key "${key}":`, e);
            continue;
          }
        }
      }
      
      // If not found in structured data, look for raw labId string
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        const value = localStorage.getItem(key);
        if (value && value.match(/^000000\d{2}$/)) {
          console.log('Found labId as raw value in key:', key, value);
          return value;
        }
      }
      
      console.error('User labId not found in localStorage');
      return null;
    } catch (error) {
      console.error('Error getting userLabId from localStorage:', error);
      return null;
    }
  };

  // Fetch current case details when modal opens
  useEffect(() => {
    const fetchCurrentCase = async () => {
      if (!show || !caseNumber) return;

      try {
        const res = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseNumber}`
        );
        setCurrentCase(res.data);

        // Pre-select current account
        if (res.data.account_holder) {
          setSelectedAccount(res.data.account_holder);
        }
      } catch (err) {
        console.error("Failed to load current case:", err);
        // Fail silently — user can still change account
      }
    };

    fetchCurrentCase();
  }, [show, caseNumber]);

  // Fetch accounts for the current lab
  const fetchAccounts = async () => {
    try {
      const userLabId = getUserLabId();
      if (!userLabId) {
        setError("User Lab ID not found. Please login again.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      
      const response = await axios.get(
        `https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${userLabId}`
      );
      
      if (response.data.success) {
        let accountsArray = response.data.data || [];
        
        // Sort accounts alphabetically by account_name
        const sortedAccounts = accountsArray.sort((a, b) =>
          (a.account_name || '').localeCompare(b.account_name || '')
        );
        
        setAccounts(sortedAccounts);

        // If current case has account_holder, pre-select it
        if (currentCase?.account_holder && sortedAccounts.length > 0) {
          const exists = sortedAccounts.some(
            (a) => a.accountId === currentCase.account_holder
          );
          if (exists) {
            setSelectedAccount(currentCase.account_holder);
          }
        }
      } else {
        setError(response.data.message || "Failed to load accounts");
      }
    } catch (err) {
      console.error("Fetch accounts error:", err);
      setError(
        err.response?.data?.message || 
        "Failed to load accounts. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch providers for selected account
  const fetchProviders = async (accountId) => {
    if (!accountId) {
      setProviders([]);
      setSelectedProvider("");
      return;
    }

    try {
      const userLabId = getUserLabId();
      if (!userLabId) {
        setProviderError("User Lab ID not found");
        return;
      }

      setProviderLoading(true);
      setProviderError(null);
      
      // Pass both accountId and userLabId to filter providers by lab
      const res = await axios.get(
        `https://hms.backend.hiragroupcloud.com/api/contacts/${accountId}?userLabId=${userLabId}`
      );
      
      let rawProviders = [];
      if (res.data?.data && Array.isArray(res.data.data))
        rawProviders = res.data.data;
      else if (Array.isArray(res.data)) rawProviders = res.data;
      else if (res.data?.contacts) rawProviders = res.data.contacts;

      if (rawProviders.length === 0) {
        setProviderError("No providers found for this account");
        setProviders([]);
        return;
      }

      const transformed = rawProviders.map((p) => ({
        id: p._id,
        name:
          `${p.firstName || ""} ${p.lastName || ""}`.trim() ||
          "Unnamed Provider",
      }));

      setProviders(transformed);

      // If current case has provider, try to pre-select it
      if (currentCase?.provider_name) {
        let providerName = currentCase.provider_name;
        
        // If provider_name is an ObjectId, we need to fetch the actual provider
        if (typeof currentCase.provider_name === 'string' && 
            currentCase.provider_name.length === 24) {
          try {
            // Try to find provider by ID
            const provider = rawProviders.find(p => p._id === currentCase.provider_name);
            if (provider) {
              const matchedProvider = transformed.find(
                (p) => p.id === provider._id
              );
              if (matchedProvider) {
                setSelectedProvider(matchedProvider.id);
                return;
              }
            }
          } catch (e) {
            console.log("Could not match provider by ID, trying by name");
          }
        }
        
        // Try to match by name
        const matchedProvider = transformed.find(
          (p) => p.name === providerName
        );
        if (matchedProvider) {
          setSelectedProvider(matchedProvider.id);
        } else if (transformed.length > 0) {
          setSelectedProvider(transformed[0].id);
        }
      } else if (transformed.length > 0) {
        setSelectedProvider(transformed[0].id);
      }
    } catch (err) {
      console.error("Fetch providers error:", err);
      setProviderError(
        err.response?.data?.message || 
        "Failed to load providers"
      );
      setProviders([]);
    } finally {
      setProviderLoading(false);
    }
  };

  // Load accounts when modal opens
  useEffect(() => {
    if (show) {
      fetchAccounts();
      setSelectedAccount("");
      setSelectedProvider("");
      setProviders([]);
      setSaveError(null);
    }
  }, [show]);

  // Load providers when selected account changes
  useEffect(() => {
    if (selectedAccount) {
      fetchProviders(selectedAccount);
    }
  }, [selectedAccount]);

  // Handle Save
  const handleSave = async () => {
    if (!caseNumber) {
      alert("Case number is missing!");
      return;
    }

    const selectedAcc = accounts.find(
      (a) => a.accountId === selectedAccount
    );
    const selectedProv = providers.find((p) => p.id === selectedProvider);

    if (!selectedAcc || !selectedProv) {
      alert("Please select both account and provider");
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const payload = {
        accountId: selectedAcc.accountId,
        providerId: selectedProv.id,
      };

      const response = await axios.put(
        `https://hms.backend.hiragroupcloud.com/api/cases/${caseNumber}/change-account`,
        payload
      );

      if (response.data.success) {
        handleConfirm({
          account: selectedAcc,
          provider: selectedProv,
          updatedCase: response.data.case,
        });
        handleClose();
      }
    } catch (err) {
      console.error("Update case account failed:", err);
      setSaveError(
        err.response?.data?.message ||
          "Failed to update case. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="md">
      {/* Header - Same as before */}
      <div
        style={{
          backgroundColor: "#004aad",
          padding: "12px 15px",
          color: "white",
          fontWeight: "bold",
          borderTopLeftRadius: "6px",
          borderTopRightRadius: "6px",
          fontSize: "18px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>Change Account</span>
        <span
          onClick={handleClose}
          style={{ cursor: "pointer", fontSize: "28px", fontWeight: "bold" }}
        >
          ×
        </span>
      </div>

      <Modal.Body style={{ padding: "20px" }}>
        {loading && (
          <div className="text-center my-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading accounts...</p>
          </div>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {saveError && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setSaveError(null)}
          >
            {saveError}
          </Alert>
        )}

        {/* Info Box - Now shows current case details */}
        <div
          style={{
            background: "linear-gradient(to bottom, #4fa6e3, #1a62b7)",
            color: "white",
            padding: "12px 15px",
            borderRadius: "6px",
            display: "flex",
            gap: "12px",
            fontSize: "13.5px",
            border: "1px solid #1d5fa5",
            alignItems: "flex-start",
            marginBottom: "20px",
          }}
        >
          <GrCircleInformation style={{ fontSize: "32px", flexShrink: 0 }} />
          <div style={{ fontWeight: "600", lineHeight: "1.4" }}>
            {currentCase ? (
              <>
                Case <strong>#{caseNumber}</strong> belongs to patient{" "}
                <strong>
                  {currentCase.first_name} {currentCase.last_name}
                </strong>
                .<br />
                Current Account: <strong>{currentCase.account_holder}</strong>
                <br />
                Current Provider:{" "}
                <strong>{currentCase.provider_name || "Not Set"}</strong>
                <br />
                Changing account will update pricing and access.
              </>
            ) : (
              <>
                This case is currently shared with an account. Changing account
                will update access and pricing.
              </>
            )}
          </div>
        </div>

        {/* Form - Exactly same as before */}
        <Form>
          <div className="row mb-3 align-items-center">
            <label className="col-4 fw-bold">
              Account <span style={{ color: "red" }}>*</span>
            </label>
            <div className="col-8">
              <Form.Select
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                disabled={loading || accounts.length === 0}
              >
                <option value="">Select Account</option>
                {accounts.map((acc) => (
                  <option
                    key={acc.accountId}
                    value={acc.accountId}
                  >
                    {acc.account_name || acc.name || acc.accountId}{" "}
                    {acc.account_type ? `(${acc.account_type})` : ""}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>

          <div className="row mb-3 align-items-center">
            <label className="col-4 fw-bold">
              Provider <span style={{ color: "red" }}>*</span>
            </label>
            <div className="col-8">
              <Form.Select
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
                disabled={
                  providerLoading || providers.length === 0 || !selectedAccount
                }
              >
                <option value="">
                  {providerLoading
                    ? "Loading providers..."
                    : providerError || "Select Provider"}
                </option>
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        </Form>
      </Modal.Body>

      {/* Footer - Exactly same */}
      <Modal.Footer
        style={{
          borderTop: "none",
          padding: "15px 20px",
          backgroundColor: "#f8f9fa",
          justifyContent: "flex-end",
        }}
      >
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={
            saving ||
            loading ||
            providerLoading ||
            !selectedAccount ||
            !selectedProvider
          }
          style={{
            background: "#004aad",
            border: "none",
            padding: "8px 30px",
            fontWeight: "bold",
          }}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeAccount;